import axios from "axios";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppDataContext, ctxDefinition } from "../components/ctx";
import { BrandButton, BrandButtonWhite } from "../components/form/buttons";
import { BrandInput } from "../components/form/inputs";
import UserMenu from "../components/userMenu";
import { ApiEndpoint } from "../components/utils";
import "./startNewExam.scss";

interface ResetResponse {
  success?: boolean;
  message?: string;
  response?: number;
}

const StartNewExam = () => {
  const { userData, clearSession } = useContext(
    AppDataContext,
  ) as ctxDefinition;
  const navigate = useNavigate();
  const [confirmation, setConfirmation] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const hasSession = Boolean(userData?.token && userData?.userid);
  const canConfirm = confirmation.toLowerCase() === "confirm";

  const openConfirmation = () => {
    setConfirmation("");
    setError("");
    setConfirmOpen(true);
  };

  const closeConfirmation = () => {
    if (submitting) return;
    setConfirmOpen(false);
    setConfirmation("");
    setError("");
  };

  const resetAndLogout = async () => {
    if (!canConfirm || submitting || !hasSession) return;
    setSubmitting(true);
    setError("");

    try {
      const resp = await axios.post<ResetResponse>(
        ApiEndpoint + "wp-json/firstchoiceprep/v1/resetOwnAccount",
        { token: userData.token },
      );
      if (!resp.data?.success) {
        throw new Error(resp.data.message ?? "Unable to reset your exam.");
      }
      clearSession();
      navigate("/login", { replace: true });
    } catch (requestError) {
      const responseMessage = axios.isAxiosError(requestError)
        ? requestError.response?.data?.message
        : undefined;
      setError(
        responseMessage ??
          (requestError instanceof Error
            ? requestError.message
            : "Unable to reset your exam. Please try again."),
      );
      setSubmitting(false);
    }
  };

  return (
    <UserMenu>
      <section className="startNewExamPage">
        <header className="startNewExamHeader">
          <h1>Start a New Exam</h1>
          <p>
            Starting a new exam will reset your current exam progress. You will
            be logged out and need to log back in to begin the new exam.
          </p>
        </header>

        {!hasSession && (
          <Alert severity="warning" className="startNewExamMessage">
            Your session is unavailable. Please sign in before starting a new
            exam.
            <BrandButton onClick={() => navigate("/login", { replace: true })}>
              SIGN IN
            </BrandButton>
          </Alert>
        )}

        <div className="startNewExamAction">
          <BrandButton disabled={!hasSession} onClick={openConfirmation}>
            Start New Exam
          </BrandButton>
        </div>

        <Dialog
          open={confirmOpen}
          onClose={closeConfirmation}
          aria-labelledby="startNewExamDialogTitle"
          aria-describedby="startNewExamDialogDescription"
          PaperProps={{ className: "startNewExamDialog" }}
        >
          <DialogTitle id="startNewExamDialogTitle">
            Confirm New Exam
          </DialogTitle>
          <DialogContent>
            <p id="startNewExamDialogDescription">
              Your current exam progress will be reset. You will then be logged
              out and need to sign back in. Type confirm to continue.
            </p>
            <BrandInput
              autoFocus
              fullWidth
              label='Type "confirm"'
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              disabled={submitting}
            />
            {error && (
              <Alert severity="error" className="startNewExamDialogError">
                {error}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <BrandButtonWhite onClick={closeConfirmation} disabled={submitting}>
              CANCEL
            </BrandButtonWhite>
            <BrandButton
              onClick={resetAndLogout}
              disabled={!canConfirm || submitting}
            >
              {submitting ? "RESETTING..." : "RESET AND LOG OUT"}
            </BrandButton>
          </DialogActions>
        </Dialog>
      </section>
    </UserMenu>
  );
};

export default StartNewExam;
